import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button as UIButton,
  TextField,
} from "@mui/material";

import { ExpandMoreRounded } from "@mui/icons-material";
import Edit2LineIcon from "remixicon-react/Edit2LineIcon";
import { useQuery } from "@tanstack/react-query";
import { Paper, Text } from "$components";
import useOrder from "$hooks/useOrder";
import useJStyles from "$hooks/useJStyles";
import { CompanyServices, EntrepotService } from "$services";
import { Loader } from "$components/Layout/Loader";
import { isAnObjectId } from "$utils/utils";
import { CacheInvalidationAction } from "$utils/cache";

type Props = {
  receptions: any;
  editReceptionClick: any;
  isReadOnly?: boolean;
};

function OrderReceptions({
  receptions,
  editReceptionClick,
  isReadOnly = false,
}: Props) {
  const { classes } = useOrder();
  const { jclasses } = useJStyles();

  const listCompaniesId = [
    ...new Set(
      receptions
        .map((item: any) => item.provider)
        .flat()
        .filter((provider) => provider && isAnObjectId(provider))
    ),
  ];
  const fetchComapnies = listCompaniesId.length > 0;
  const { data: companies, isLoading: companiesLoading } = useQuery({
    queryKey: [CacheInvalidationAction.GET_COMPANIES, listCompaniesId],
    queryFn: () => CompanyServices.getCompanies({ $inId: listCompaniesId }),
    enabled: fetchComapnies,
    staleTime: 60 * 1000,
  });

  const listWarehouseId = [
    ...new Set(
      receptions
        .map((item: any) => item.warehouse)
        .flat()
        .filter((f) => f)
    ),
  ];
  const fetchWarehouses = listWarehouseId.length > 0;
  const { data: warehouses, isLoading: warehouseLoading } = useQuery({
    queryKey: [CacheInvalidationAction.GET_ENTREPOTS, listWarehouseId],
    queryFn: () => EntrepotService.getEntrepots({ $inId: listWarehouseId }),
    enabled: fetchWarehouses,
    staleTime: 60 * 1000,
  });

  if (companiesLoading || warehouseLoading) {
    <Loader />;
  }

  return (
    <Accordion
      style={{ marginTop: 20, boxShadow: "none" }}
      defaultExpanded={isReadOnly}
      disabled={!receptions.length}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreRounded />}
        aria-controls="panelReception-content"
        id="panelReception-header"
      >
        <div
          className={jclasses.formSectionHead}
          style={{ margin: 0, width: "100%" }}
        >
          <Text type="h3">Réception(s)</Text>
          <Text type="text">
            {!receptions?.length && "Pas de réceptions associées"}

            {receptions?.length > 1 && `${receptions?.length} réception(s)`}
          </Text>
        </div>
      </AccordionSummary>
      <AccordionDetails>
        <div style={{ width: "100%" }}>
          {receptions?.map((item) => {
            return (
              <Paper
                key={item.id}
                className={classes.paperForm}
                style={{ marginTop: 20, marginBottom: 0 }}
              >
                <div className={classes.paperFormHead}>
                  <Text type="text">{item.reference}</Text>
                  {!isReadOnly && (
                    <UIButton
                      variant="text"
                      style={{ textTransform: "none" }}
                      onClick={() => editReceptionClick(item)}
                    >
                      <Edit2LineIcon />
                    </UIButton>
                  )}
                </div>
                <div className={classes.paperFormBody}>
                  <div className={classes.paperFormBodyTop}>
                    <div style={{ marginRight: 80 }}>
                      <div className={classes.fieldsRow}>
                        {/* Id reception - autoincrement */}
                        <div className={classes.fieldsCol}>
                          <TextField
                            id="reference"
                            label="Id réception"
                            variant="outlined"
                            value={item.reference}
                            disabled
                          />
                        </div>

                        {/* Date - date picker */}
                        <div className={classes.fieldsCol}>
                          <TextField
                            id="Date de réception"
                            name="receptionDate"
                            label="Date de réception"
                            variant="outlined"
                            value={new Date(
                              item.receptionDate
                            ).toLocaleDateString()}
                            disabled
                          />
                        </div>

                        {/* Réference de l'entrepot de réception - select */}
                        <div className={classes.fieldsCol}>
                          <TextField
                            id="warehouseReference"
                            name="warehouseReference"
                            label="ID réception entrepôt"
                            variant="outlined"
                            value={item.warehouseReference}
                            disabled
                          />
                        </div>
                      </div>

                      <div className={classes.fieldsRow}>
                        {/* Fournisseur - string */}
                        {item?.isTransfer && (
                          <div className={classes.fieldsCol}>
                            <TextField
                              id="provider"
                              name="provider"
                              label="Entreprôt de réception"
                              variant="outlined"
                              value={
                                (warehouses?.data || [])?.find(
                                  (c: any) => c._id === item?.warehouse
                                )?.name
                              }
                              disabled
                            />
                          </div>
                        )}
                        {item?.isTransfer === false && (
                          <div className={classes.fieldsCol}>
                            <TextField
                              id="provider"
                              name="provider"
                              label="Fournisseur de réception"
                              variant="outlined"
                              value={
                                // warning: provider can be an id or a name in DB
                                (companies?.data || [])?.find(
                                  (c: any) =>
                                    c._id === item.provider ||
                                    c.name === item.provider
                                )?.name || item?.provider
                              }
                              disabled
                            />
                          </div>
                        )}

                        {/* N° BL, modifiable */}
                        <div className={classes.fieldsCol}>
                          <TextField
                            label="N° de bon de livraison"
                            variant="outlined"
                            value={item.blNumber}
                            disabled
                          />
                        </div>

                        <div className={classes.fieldsCol} />
                      </div>

                      <TextField
                        variant="outlined"
                        multiline
                        minRows={3}
                        style={{ width: "100%" }}
                        value={item.notes}
                        disabled
                        label="Notes"
                      />
                    </div>
                  </div>
                </div>
              </Paper>
            );
          })}
        </div>
      </AccordionDetails>
    </Accordion>
  );
}

export default OrderReceptions;
